import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { Button } from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import Breadcrumb from '../../../components/Breadcrumb';
import { PRODUCTMANAGE_PAGE } from '../../../routes/routeConstant';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { FileUpload } from '../../../components/InputImage';
import useOtherProduct from '../../../stores/useOtherProduct';
import { IOtherProduct } from '../../../types/types';
import useServices from '../../../stores/useServices';
import InputEn from '../../../components/InputEn';

export default function FormOther() {
  const backLink = `${PRODUCTMANAGE_PAGE}?page=Other`;
  const breadcrumbPath = [{ name: 'Other', link: backLink }, { name: 'Form Other' }];
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, saveLoading, fetchProducts, editProduct, addProduct } = useOtherProduct();
  const { services, fetchServices } = useServices();
  const editId = searchParams.get('edit');
  const [currentImage, setCurrentImage] = useState<string>('/assets/no-data-image.svg');

  const { register, handleSubmit, setValue, watch, reset } = useForm<IOtherProduct>({
    defaultValues: {
      name_prd: '',
      name_prd_en: '',
      price_prd: undefined,
      image_prd: '',
    },
  });

  useEffect(() => {
    fetchProducts();
    fetchServices();
  }, [fetchProducts, fetchServices]);

  useEffect(() => {
    if (editId) {
      const productToEdit = products?.find((product) => product.product_id === Number(editId));
      if (productToEdit) {
        setValue('name_prd', productToEdit.name_prd);
        setValue('name_prd_en', productToEdit.name_prd_en);
        setValue('price_prd', Number(productToEdit.price_prd));
        setValue('service_id', productToEdit.service?.service_id);
        setValue('image_prd', productToEdit.image_prd);
        setValue('detail_prd', productToEdit.detail_prd);
        setValue('detail_prd_en', productToEdit.detail_prd_en);
        setCurrentImage(productToEdit.image_prd || '/assets/no-data-image.svg');
      }
    }
  }, [editId, products, setValue]);

  const onSubmit = async (data: IOtherProduct) => {
    const modifiedData = {
      ...data,
      price_prd: Number(data.price_prd),
    };
    const productToEdit = products?.find((product) => product.product_id === Number(editId));
    if (editId && productToEdit !== undefined) {
      await editProduct(Number(editId), modifiedData);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('tab', 'list');
        return newParams;
      });
    } else {
      await addProduct(modifiedData);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('tab', 'list');
        return newParams;
      });
    }
    reset();
    setCurrentImage('/assets/no-data-image.svg');
  };

  return (
    <div className="flex gap-4 flex-col">
      <Breadcrumb paths={breadcrumbPath} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col text-sm gap-2">
            <p className="flex gap-1">
              Name <span className="text-red-600">*</span>
            </p>
            <InputEn
              type="text"
              nama="name_prd"
              namaEn="name_prd_en"
              placeholder="Masukkan nama produk..."
              register={register}
              watch={watch}
              setValue={setValue}
            />
          </div>

          <div className="flex flex-col text-sm gap-2">
            <p className="flex gap-1">
              Price <span className="text-red-600">*</span>
            </p>
            <Input {...register('price_prd')} type="number" placeholder="Harga mulai dari..." />
          </div>

          <div className="flex flex-col text-sm gap-2">
            <p className="flex gap-1">
              Service <span className="text-red-600">*</span>
            </p>
            <Select
              value={watch('service_id') ? String(watch('service_id')) : undefined}
              onValueChange={(value) => setValue('service_id', Number(value))}
              required
            >
              <SelectTrigger className="focus:outline-none focus:ring-1 focus:ring-blue-500">
                <SelectValue placeholder="Pilih layanan" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {services?.map((srv) => (
                    <SelectItem key={srv.service_id} value={String(srv.service_id)}>
                      {srv.name_srv_id}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col text-sm gap-2">
            <p className="flex gap-1">Detail</p>
            <InputEn
              type="texteditor"
              nama="detail_prd"
              namaEn="detail_prd_en"
              placeholder="Masukkan detail produk..."
              register={register}
              watch={watch}
              setValue={setValue}
              required={false}
            />
          </div>

          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <FileUpload
              setValue={setValue}
              register={register}
              imageFieldName="image_prd"
              defaultImage={currentImage}
            />
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" type="button">
              <Link to={backLink}>Batal</Link>
            </Button>
            <Button type="submit" disabled={saveLoading}>
              {saveLoading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
